import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ChevronRight, Download, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import GatedDownloadModal from './GatedDownloadModal';
import VideoModal from './VideoModal';
import useCases from '../data/useCases';

const UseCases = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [gatedAsset, setGatedAsset] = useState(null); // { title, url } or null
  const [videoAsset, setVideoAsset] = useState(null); // { url, title } or null

  const openGatedPdf = (title, url) => {
    setIsAutoPlaying(false);
    if (!url) return;
    setGatedAsset({ title, url });
  };


  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % useCases.length);
    }, 15000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % useCases.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev - 1 + useCases.length) % useCases.length);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
  };

  const currentCase = useCases[currentSlide];
  const secondCase = useCases[(currentSlide + 1) % useCases.length];
  const secondIndex = (currentSlide + 1) % useCases.length;

  const renderCard = (useCase, slideIndex) => {
    const CardIcon = useCase.icon;
    const cardHasPdfs = useCase.pdfs && useCase.pdfs.length > 0;
    return (
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl shadow-2xl overflow-hidden flex flex-col h-full">
        {/* Image */}
        <div
          className={`relative h-72 flex-shrink-0 ${useCase.videoUrl ? 'cursor-pointer group' : ''}`}
          onClick={() => {
            if (useCase.videoUrl) {
              setIsAutoPlaying(false);
              setVideoAsset({ url: useCase.videoUrl, title: useCase.title });
            }
          }}
        >
          <img
            src={useCase.image}
            alt={useCase.title}
            className={`w-full h-full object-cover ${useCase.videoUrl ? 'group-hover:scale-105 transition-transform duration-500' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent"></div>
          {useCase.videoUrl && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-2xl">
                <svg className="h-8 w-8 text-[#1e2d3b] ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          )}
          <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-3">
            <p className="text-slate-900 font-semibold text-xs sm:text-sm">Case Study: {useCase.caseStudy}</p>
          </div>
          <div className="absolute top-4 right-4 bg-white text-blue-600 px-3 py-1 rounded-full font-bold text-xs shadow-lg">
            {slideIndex + 1} / {useCases.length}
          </div>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6 md:p-8 flex flex-col text-white flex-grow">
          <div className="flex items-center space-x-3 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <CardIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold">{useCase.title}</h3>
          </div>

          <p className="text-sm sm:text-base text-white/95 leading-relaxed mb-5 sm:mb-6">{useCase.description}</p>

          <div className="space-y-3 mb-6 flex-grow">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-white/80">
              Use Case Highlights
            </h4>
            <div className="grid gap-2">
              {useCase.applications.map((app, idx) => {
                const isHighlight = typeof app === 'object' && app.highlight;
                const text = typeof app === 'object' ? app.text : app;
                return (
                  <div key={text} className="flex items-start space-x-2">
                    <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-orange-400"></div>
                    <span
                      className={
                        isHighlight
                          ? 'text-orange-400 text-base font-extrabold tracking-tight'
                          : 'text-white/90 text-sm'
                      }
                    >
                      {text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            {cardHasPdfs ? (
              useCase.pdfs.map((pdf, idx) => (
                <Button
                  key={pdf.url}
                  onClick={() => openGatedPdf(pdf.title, pdf.url)}
                  data-testid={`usecase-pdf-btn-${slideIndex}-${idx}`}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-4 sm:py-5 text-sm sm:text-base shadow-xl"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {pdf.label}
                </Button>
              ))
            ) : (
              <Button
                disabled
                className="w-full bg-gradient-to-r from-orange-500/60 to-orange-600/60 text-white font-semibold py-4 sm:py-5 text-sm sm:text-base shadow-xl cursor-not-allowed"
              >
                <Download className="mr-2 h-4 w-4" />
                PDF coming soon
              </Button>
            )}

            {useCase.videoUrl && (
              <Button
                onClick={() => {
                  setIsAutoPlaying(false);
                  setVideoAsset({ url: useCase.videoUrl, title: useCase.title });
                }}
                className="w-full bg-[#1e2d3b] hover:bg-[#15202b] text-white font-semibold py-4 sm:py-5 text-sm sm:text-base shadow-xl"
              >
                <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Watch the Video
              </Button>
            )}

            {useCase.slug && (
              <Link
                to={`/use-cases/${useCase.slug}`}
                className="w-full inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white font-semibold py-3 sm:py-4 text-sm sm:text-base rounded-md border border-white/30 transition-colors"
                data-testid={`usecase-view-page-${useCase.slug}`}
              >
                View full case study
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="use-cases" className="py-14 sm:py-20 md:py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e2d3b] mb-4">
            Use <span className="text-blue-600">Cases</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-orange-500 mx-auto"></div>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Visible slide(s): 1 on mobile, 2 side-by-side on lg+ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {renderCard(currentCase, currentSlide)}
            <div className="hidden lg:block">
              {renderCard(secondCase, secondIndex)}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 w-10 h-10 bg-white/90 hover:bg-white text-blue-600 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 backdrop-blur-sm"
            aria-label="Previous use case"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 w-10 h-10 bg-white/90 hover:bg-white text-blue-600 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 backdrop-blur-sm"
            aria-label="Next use case"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center items-center space-x-2 mt-8">
          {useCases.map((uc, index) => (
            <button
              key={uc.slug}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide
                  ? 'w-8 h-2 bg-blue-600 shadow-md'
                  : 'w-2 h-2 bg-slate-300 hover:bg-blue-400 hover:scale-125'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Instruction Text */}
        <div className="text-center mt-6">
          <p className="text-slate-500 text-sm">
            Click the arrows or dots to explore all use cases
          </p>
        </div>

        {/* Hidden index (sr-only) — kept in the DOM so search + AI crawlers
            get a real hyperlink to each dedicated case-study page. */}
        <div className="sr-only" data-testid="usecases-index-hidden">
          <h3>Explore every case study</h3>
          <ul>
            {useCases.map((u) => (
              <li key={u.slug}>
                <Link to={`/use-cases/${u.slug}`} data-testid={`usecase-index-link-${u.slug}`}>{u.title}</Link> — {u.caseStudy}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Gated download modal */}
      <GatedDownloadModal
        open={Boolean(gatedAsset)}
        onOpenChange={(open) => {
          if (!open) setGatedAsset(null);
        }}
        resourceTitle={gatedAsset?.title}
        resourceUrl={gatedAsset?.url}
      />

      {/* Video modal */}
      <VideoModal
        open={Boolean(videoAsset)}
        onOpenChange={(open) => {
          if (!open) setVideoAsset(null);
        }}
        videoUrl={videoAsset?.url}
        title={videoAsset?.title}
      />
    </section>
  );
};

export default UseCases;
